import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import LabelIcon from "@mui/icons-material/Label";
import GridViewIcon from "@mui/icons-material/GridView";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import { useToast } from "../../../../../Hooks/UseToast";
import { logEventos } from "../../../../../services/services";
import { fetchSucursales } from "../../../../RRHH2/features/legajos/services/empleados.api";
import { getSemana } from "../../../common/utils/semana";
import { imprimirEtiquetas, imprimirEtiquetasSemana } from "../service/etiquetasService";

const hoy = () => new Date().toISOString().slice(0, 10);
const semanaActual = getSemana(0);

const EtiquetasPage = () => {
  const [modo, setModo] = useState("dia");
  const [fecha, setFecha] = useState(hoy());
  const [desde, setDesde] = useState(semanaActual.desde);
  const [hasta, setHasta] = useState(semanaActual.hasta);
  const [tipo, setTipo] = useState("individual");
  const [idSucursal, setIdSucursal] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [generando, setGenerando] = useState(false);
  const { showToast, Toast } = useToast();

  useEffect(() => {
    fetchSucursales()
      .then((res) => setSucursales(res || []))
      .catch(() => setSucursales([]));

    logEventos(
      "acceso al menú Viandas Etiquetas",
      "Viandas",
      "imprimir Etiquetas",
      localStorage.getItem("user"),
    );
  }, []);

  const handleImprimir = async () => {
    setGenerando(true);
    try {
      const resultado =
        modo === "semana"
          ? await imprimirEtiquetasSemana({
              desde,
              hasta,
              id_sucursal: idSucursal || undefined,
            })
          : await imprimirEtiquetas({
              fecha,
              id_sucursal: idSucursal || undefined,
              type: tipo === "a4" ? "A4" : undefined,
            });

      if (resultado.sinDatos) {
        showToast("No hay pedidos de Comida al Paso para ese rango.", "info");
      } else if (!resultado.abierta) {
        showToast(
          "El navegador bloqueó la nueva pestaña. Permitila para poder imprimir.",
          "warning",
        );
      }
    } catch (err) {
      showToast(err.message || "Error al generar las etiquetas.", "error");
    } finally {
      setGenerando(false);
    }
  };

  const puedeGenerar = modo === "semana" ? Boolean(desde && hasta) : Boolean(fecha);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
          Imprimir etiquetas de pedidos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Genera un PDF con una etiqueta por pedido (nombre del empleado, menú, proveedor y
          fecha) listo para imprimir.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          maxWidth: 480,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Rango a imprimir
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={modo}
            onChange={(e, val) => val && setModo(val)}
          >
            <ToggleButton value="dia" sx={{ textTransform: "none", gap: 1 }}>
              <LabelIcon fontSize="small" />
              Un día
            </ToggleButton>
            <ToggleButton value="semana" sx={{ textTransform: "none", gap: 1 }}>
              <CalendarViewWeekIcon fontSize="small" />
              Semana (Comida al Paso)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {modo === "dia" ? (
          <TextField
            type="date"
            label="Fecha"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        ) : (
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              type="date"
              label="Desde"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
            <TextField
              type="date"
              label="Hasta"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Box>
        )}

        <FormControl size="small">
          <Select
            displayEmpty
            value={idSucursal}
            onChange={(e) => setIdSucursal(e.target.value)}
          >
            <MenuItem value="">
              <em>Todas las sucursales</em>
            </MenuItem>
            {sucursales.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {modo === "dia" && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Formato de impresión
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={tipo}
              onChange={(e, val) => val && setTipo(val)}
            >
              <ToggleButton value="individual" sx={{ textTransform: "none", gap: 1 }}>
                <LabelIcon fontSize="small" />
                Individuales (62×30mm)
              </ToggleButton>
              <ToggleButton value="a4" sx={{ textTransform: "none", gap: 1 }}>
                <GridViewIcon fontSize="small" />
                Grilla en hoja A4
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {modo === "semana" && (
          <Typography variant="caption" color="text.secondary">
            Solo incluye pedidos de Comida al Paso, de lunes a viernes, con un salto de hoja
            por día.
          </Typography>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={<LocalPrintshopIcon />}
          onClick={handleImprimir}
          disabled={!puedeGenerar || generando}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700, py: 1.2 }}
        >
          {generando ? "Generando..." : "Generar e imprimir"}
        </Button>
      </Paper>

      {Toast}
    </Box>
  );
};

export default EtiquetasPage;
